import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { checkUserHasAccess, sideBarActiveBgColor } from "../utility/Common";
import { RoutePaths } from "../utility/RoutePaths";

const Sidebar = ({ isOpen = false, setIsOpen }) => {
  const location = useLocation();
  const { logout, user, userLevelId } = useAuth();
  const [expandedItems, setExpandedItems] = useState({});

  const navigation = useMemo(
    () => [
      {
        name: "Dashboard",
        href: RoutePaths.ADMIN_DASHBOARD_PAGE,
        icon: "📊",
        userLevelAccess: [1, 2],
      },
      {
        name: "Dashboard",
        href: RoutePaths.STUDENT_DASHBOARD_PAGE,
        icon: "📊",
        userLevelAccess: [3],
      },
      {
        name: "Classes",
        href: RoutePaths.ADMIN_CLASS_PAGE,
        icon: "🏫",
        userLevelAccess: [1],
      },
      {
        name: "Class Schedules",
        href: RoutePaths.ADMIN_CLASS_SCHEDULE_PAGE,
        icon: "🕒",
        userLevelAccess: [1, 2],
      },
      {
        name: "Manage Staffs",
        href: RoutePaths.ADMIN_STAFF_MANAGE_PAGE,
        icon: "🧑‍🏫",
        userLevelAccess: [1],
      },
      {
        name: "Manage Students",
        href: RoutePaths.ADMIN_STUDENT_MANAGE_PAGE,
        icon: "👥",
        userLevelAccess: [1, 2],
      },
      {
        name: "Bank Transfers",
        href: RoutePaths.ADMIN_BANK_TRANSFER_MANAGE_PAGE,
        icon: "💰",
        userLevelAccess: [1, 2],
      },
      {
        name: "Manage Banner",
        href: RoutePaths.ADMIN_BANNER_MANAGE_PAGE,
        icon: "🖼️",
        userLevelAccess: [1, 2],
      },
      {
        name: "Manage Past Papers",
        href: RoutePaths.ADMIN_MANAGE_PAST_PAPERS_PAGE,
        icon: "📄",
        userLevelAccess: [1, 2],
      },
      {
        name: "Manage Seminars",
        href: RoutePaths.ADMIN_MANAGE_SEMINARS_PAGE,
        icon: "🎤",
        userLevelAccess: [1, 2],
      },
      {
        name: "Manage Courses",
        href: RoutePaths.ADMIN_MANAGE_COURSES_PAGE,
        icon: "📚",
        userLevelAccess: [1, 2],
      },
      {
        name: "Profile",
        href: RoutePaths.STUDENT_PROFILE_PAGE,
        icon: "👤",
        userLevelAccess: [3],
      },
      {
        name: "Enrolled Classes",
        href: RoutePaths.STUDENT_ENROLLED_CLASSES_PAGE,
        icon: "📚",
        userLevelAccess: [3],
      },
      {
        name: "Payment History",
        href: RoutePaths.STUDENT_PAYMENT_HISTORY_PAGE,
        icon: "💰",
        userLevelAccess: [3],
      },
      {
        name: "My Study Packs",
        href: RoutePaths.STUDENT_STUDY_PACKS_PAGE,
        icon: "📖",
        userLevelAccess: [3],
      },
      {
        name: "Past Papers",
        href: RoutePaths.STUDENT_PAST_PAPERS_PAGE,
        icon: "📄",
        userLevelAccess: [3],
      },
      {
        name: "Free Classes",
        href: RoutePaths.STUDENT_FREE_CLASSES_PAGE,
        icon: "🎓",
        userLevelAccess: [3],
      },
      {
        name: "Seminars",
        href: RoutePaths.STUDENT_SEMINARS_PAGE,
        icon: "🎤",
        userLevelAccess: [3],
      },
      {
        name: "Courses",
        href: RoutePaths.STUDENT_COURSES_PAGE,
        icon: "📚",
        userLevelAccess: [3],
      }
    ],
    [],
  );

  const isActive = useCallback(
    (path) => {
      return location.pathname === path;
    },
    [location.pathname],
  );

  const hasActiveChild = useCallback(
    (item) => {
      if (!item.children) return false;
      return item.children.some(
        (child) =>
          child.href &&
          isActive(child.href) &&
          checkUserHasAccess(child.userLevelAccess, userLevelId),
      );
    },
    [isActive, userLevelId],
  );

  // Auto-expand items when their children are active
  useEffect(() => {
    navigation.forEach((item) => {
      if (item.children && hasActiveChild(item)) {
        setExpandedItems((prev) => ({
          ...prev,
          [item.name]: true,
        }));
      }
    });
  }, [location.pathname, userLevelId, navigation, hasActiveChild]);

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (setIsOpen && window.innerWidth < 768) {
      setIsOpen(false);
    }
  }, [location.pathname, setIsOpen]);

  const toggleExpand = (itemName) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemName]: !prev[itemName],
    }));
  };

  const renderNavItem = (item) => {
    if (!checkUserHasAccess(item.userLevelAccess, userLevelId)) return null;

    // If item has children, render as expandable
    if (item.children) {
      const isExpanded = expandedItems[item.name];
      const hasActive = hasActiveChild(item);

      return (
        <div key={item.name}>
          <button
            onClick={() => toggleExpand(item.name)}
            className={`flex items-center justify-between w-full px-4 py-3 rounded-lg transition duration-200 text-sm ${
              hasActive ? "bg-primary-600 text-white" : "text-black"
            }`}
          >
            <div className="flex items-center">
              <span className="text-sm mr-3">{item.icon}</span>
              {item.name}
            </div>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${
                isExpanded ? "transform rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {isExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children.map(
                (child) =>
                  checkUserHasAccess(item.userLevelAccess, userLevelId) && (
                    <Link
                      key={child.name}
                      to={child.href}
                      onClick={() =>
                        setIsOpen && window.innerWidth < 768 && setIsOpen(false)
                      }
                      className={`flex items-center px-4 py-2 rounded-lg transition duration-200 text-sm ${
                        isActive(child.href)
                          ? "bg-primary-600 text-white"
                          : "text-gray-700 hover:bg-primary-200 hover:text-white"
                      }`}
                    >
                      <span className="text-sm mr-3">{child.icon}</span>
                      {child.name}
                    </Link>
                  ),
              )}
            </div>
          )}
        </div>
      );
    }

    // Regular nav item
    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={() => setIsOpen && window.innerWidth < 768 && setIsOpen(false)}
        className={`flex items-center px-4 py-3 rounded-lg transition duration-200 text-sm ${
          isActive(item.href)
            ? sideBarActiveBgColor
            : "text-black hover:bg-primary-200 hover:text-black"
        }`}
      >
        <span className="text-sm mr-3">{item.icon}</span>
        {item.name}
      </Link>
    );
  };

  return (
    <>
      {/* Backdrop overlay for mobile */}
      {isOpen && setIsOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed md:static inset-y-0 left-0 z-50 bg-white text-white w-64 min-h-screen p-4 flex flex-col transform transition-transform duration-300 ease-in-out ${
          isOpen || !setIsOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
      >
        <div className="flex items-center justify-between pb-2 pt-2 border-b border-gray-200">
          <Link
            to="/"
            className="flex-shrink-0 flex items-center"
            onClick={() =>
              setIsOpen && window.innerWidth < 768 && setIsOpen(false)
            }
          >
            <span className="ml-2 text-2xl font-bold text-primary-800">
              Royal Econ
            </span>
          </Link>
          {/* Close button for mobile */}
          {setIsOpen && (
            <button
              onClick={() => setIsOpen(false)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              aria-label="Close sidebar"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        <nav className="space-y-2 mt-4">
          {navigation.map((item) => renderNavItem(item))}
        </nav>

        <div className="mt-auto pt-2 border-t border-gray-200">
          <div className="flex items-center px-4 py-3">
            <div className="flex-shrink-0 h-8 w-8">
              <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-sm font-medium text-primary-600">
                  {user?.FirstName
                    ? user.FirstName.split(" ")
                        .map((n) => n[0])
                        .join("")
                    : ""}
                </span>
              </div>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.FirstName}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.MobileNo}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center justify-center w-full px-4 py-3 text-sm text-gray-700 border border-gray-300 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-400 rounded-lg transition duration-200"
          >
            <svg
              className="w-4 h-4 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Logout
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
